// How big a custom widget's tile is: grown to fit what an agent's code draws,
// unless someone chose its size or locked it. Kept apart from the widget
// itself, which needs a browser, so it can be tested on its own.

import type { Item, WidgetItem } from "../../shared/types";
import type { RenderReport } from "../../shared/widgetKit";
import { GRID } from "../../shared/catalog";

/**
 * The size an agent's widget grows to when its content runs past its tile:
 * down, or right, into free ground, by what the frame measured, once for each
 * version of its code. Never for a tile whose size someone chose (a handle
 * drag, the Inspector, or a size asked of an agent), one the user locked in
 * place, a person's own widget, or one that would land on a neighbor.
 */
export function grownSize(w: WidgetItem, r: RenderReport, hash: string, others: Item[]): { w: number; h: number } | null {
  const p = w.props as { fitted?: unknown; sized?: unknown };
  if (!w.by || w.by === "You" || w.locked || r.scrolls || p.sized === true || p.fitted === hash) return null;
  const tall = r.sh > r.h + 6;
  const wide = r.sw > r.w + 6;
  if (!tall && !wide) return null;
  const snap = (v: number) => Math.ceil(v / GRID) * GRID;
  const nw = wide ? Math.min(snap(w.w + (r.sw - r.w) + 8), Math.max(w.w, 1600), w.w * 2) : w.w;
  const nh = tall ? Math.min(snap(w.h + (r.sh - r.h) + 8), Math.max(w.h, 1200), Math.round(w.h * 2.5)) : w.h;
  if (nw === w.w && nh === w.h) return null;
  const gap = 16;
  const near = others.filter((i) => i.boardId === w.boardId && i.id !== w.id && i.kind === "widget");
  if (near.some((i) => w.x < i.x + i.w + gap && w.x + nw + gap > i.x && w.y < i.y + i.h + gap && w.y + nh + gap > i.y)) return null;
  return { w: nw, h: nh };
}

/** An item at a size someone chose. A custom widget's is then its own: the board stops fitting it to its content. */
export function sizedTo<T extends Item>(item: T, w: number, h: number): T {
  const next = { ...item, w, h };
  if (item.kind !== "widget" || item.type !== "custom" || (w === item.w && h === item.h)) return next;
  return { ...next, props: { ...item.props, sized: true } };
}

/**
 * The tile a widget is drawn in, when it is its own tile on the board at the
 * widget's own size. Anywhere else, a phone's column say, it draws at another
 * size, and how it fits there says nothing about its tile on the board.
 */
export function boardTile(el: { offsetWidth: number; offsetHeight: number; classList: { contains(c: string): boolean } } | null | undefined, item: { w: number; h: number }): { w: number; h: number } | null {
  if (!el || el.classList.contains("mobile-item")) return null;
  return el.offsetWidth === item.w && el.offsetHeight === item.h ? { w: item.w, h: item.h } : null;
}
